/*
 * File: PatientCreatePage.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Controls loading and populating a new patient/account form
 */
import React, { Component } from "react";
import FormBuilder from "components/form-builder";
import DemographicFormModel from "../store/DemographicFormModel";
import { applySnapshot } from "mobx-state-tree";
import References from "services/references/References";
import Users from "services/users/Users";
import UIProgress from "components/ui/UIProgress";
import ProviderOfficeFeeFormModel from "../../provider_office_fee/store/ProviderOfficeFeeFormModel";
import FormStore from "components/form-builder/FormStore";
import PropTypes from "prop-types";
import { StoreContext } from "utils/useStore";

class PatientCreatePage extends Component {
  state = { loading: true };

  static contextType = StoreContext;

  constructor(props) {
    super(props);
    this.formFields = Array.from(DemographicFormModel);
    this.formStore = FormStore.create();
  }

  componentDidMount() {
    this.addProviderOfficeFeeFields();
    this.populateSelectFields();
    this.formStore.setFields(this.formFields);
  }

  addProviderOfficeFeeFields() {
    this.formFields = this.formFields.concat(ProviderOfficeFeeFormModel);
  }

  // populate select elements with values
  async populateSelectFields() {
    const references = this.context.getStore(References);
    const offices = await references.forSelect("offices");
    const feeSchedules = await references.forSelect("feeSchedules");
    const providers = await this.context.getStore(Users).forSelect("providers");
    const maritalStatus = await references.forSelect("maritalStatusTypes");
    const employments = await references.forSelect("employmentStatusTypes");
    const languages = await references.forSelect("languages");
    const relationships = await references.forSelect("relationshipTypes");

    this.setFormOptions("patientOffice", offices);
    this.setFormOptions("feeSchedule", feeSchedules);
    this.setFormOptions("patientProvider", providers);
    this.setFormOptions("accountProvider", providers);
    this.setFormOptions("maritalStatus", maritalStatus);
    this.setFormOptions("employment", employments);
    this.setFormOptions("preferredLanguageId", languages);
    this.setFormOptions("relationshipToPrimary", relationships);
    this.setFormOptions("relationshipToSecondary", relationships);

    // if creating a new account
    if (this.props.createNewAccount) {
      this.props.patient.resetAccount();
      await this.props.patient.setDefaultPatientInfo();
    }
    this.removeAccountProviderField();

    this.setState({
      loading: false
    });
  }

  setFormOptions(key, val) {
    let fieldModel = this.formStore.getFieldByName(key);
    fieldModel.setFormOptions(val);
  }

  /**
    Hide the account provider select field for the create patient form
   */
  removeAccountProviderField() {
    let fieldModel = this.formStore.getFieldByName("accountProvider");
    fieldModel.setHidden(!this.props.createNewAccount);
  }

  onChange = (key, val) => {
    if (key === "phone1" || key === "phone2" || key === "phone3") {
      this.props.patient.updatePhone(key, val);
    } else if (
      key === "phone1Type" ||
      key === "phone2Type" ||
      key === "phone3Type"
    ) {
      this.props.patient.updatePhoneType(key, val);
    } else {
      this.props.patient.update(key, val);
    }
  };

  onSubmit = async event => {
    if (event.target.name === "PatientCreate") {
      let newPatientId = await this.props.patient.saveNewPatient();
      applySnapshot(this.props.patient, {});
      this.closeForm();
      if (newPatientId) this.props.parentCallBack(newPatientId.patientId);
    }
  };

  onCancel = () => {
    this.closeForm();
  };

  closeForm = () => {
    this.props.toggleFormDialog();
  };

  render() {
    let { patient } = this.props;
    let { loading } = this.state;

    return (
      <div>
        {loading && <UIProgress />}
        {!loading && (
          <FormBuilder
            formStore={this.formStore}
            data={patient}
            parentOnChange={this.onChange}
            parentOnSubmit={this.onSubmit}
            parentOnCancel={this.onCancel}
            formName={"PatientCreate"}
          />
        )}
      </div>
    );
  }
}

PatientCreatePage.propTypes = {
  patient: PropTypes.object.isRequired,
  parentCallBack: PropTypes.func.isRequired,
  toggleFormDialog: PropTypes.func.isRequired,
  createNewAccount: PropTypes.bool
};

export default PatientCreatePage;
